import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherPlus, FeatherTrash2 } from "@subframe/core";

import type { EmployeeAllergyDto } from "@/generated/model/employeeAllergyDto";
import { useUpdateEmployeeAllergies } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import {
  allergiesFormValues,
  allergiesRequest,
  allergiesSchema,
  type AllergyFormRow,
  MAX_ALLERGIES,
  newAllergyRow,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import { SectionTextArea, SectionTextField } from "./ProfileSectionFields";

interface EditAllergiesFormProps {
  nurseryId: string;
  userId: string;
  allergies: EmployeeAllergyDto[];
  onDone: () => void;
}

export function EditAllergiesForm({
  nurseryId,
  userId,
  allergies,
  onDone,
}: EditAllergiesFormProps) {
  const { values, setErrors, setValues, errors, validate, clearErrors } =
    useProfileSectionForm(
      () => allergiesFormValues(allergies),
      allergiesSchema,
    );
  const update = useUpdateEmployeeAllergies({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Allergies",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;
  const rows = values.allergies;

  const setRows = (next: (rows: AllergyFormRow[]) => AllergyFormRow[]) =>
    setValues((current) => ({ allergies: next(current.allergies) }));
  // Errors are keyed by row position, which adding or removing a row shifts.
  const changeRows = (next: (rows: AllergyFormRow[]) => AllergyFormRow[]) => {
    clearErrors();
    setRows(next);
  };
  const setRowField = (
    key: string,
    field: "allergen" | "severityNotes",
    value: string,
  ) =>
    setRows((current) =>
      current.map((row) =>
        row.key === key ? { ...row, [field]: value } : row,
      ),
    );

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({ nurseryId, userId, data: allergiesRequest(values) });
  };

  return (
    <ProfileSectionForm
      testId="edit-allergies-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      {errors.allergies && <Alert variant="error" title={errors.allergies} />}
      {rows.length === 0 && (
        <span className="text-body text-subtext-color">
          No allergies recorded. Saving with none clears the list.
        </span>
      )}
      {rows.map((row, index) => (
        <div
          key={row.key}
          className="flex w-full flex-col gap-3 rounded-md border border-solid border-secondary-beige-200 p-4"
          data-testid="allergy-row"
        >
          <div className="flex w-full items-start gap-2">
            <div className="flex grow flex-col">
              <SectionTextField
                label={`Allergen ${index + 1}`}
                name={`allergies.${index}.allergen`}
                value={row.allergen}
                onChange={(value) => setRowField(row.key, "allergen", value)}
                disabled={isPending}
                error={errors[`allergies.${index}.allergen`]}
              />
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherTrash2 />}
              aria-label={`Remove allergen ${index + 1}`}
              disabled={isPending}
              onClick={() =>
                changeRows((current) =>
                  current.filter((r) => r.key !== row.key),
                )
              }
            />
          </div>
          <SectionTextArea
            label="Severity notes"
            name={`allergies.${index}.severityNotes`}
            value={row.severityNotes}
            onChange={(value) => setRowField(row.key, "severityNotes", value)}
            disabled={isPending}
            error={errors[`allergies.${index}.severityNotes`]}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="neutral-secondary"
        size="medium"
        icon={<FeatherPlus />}
        disabled={isPending || rows.length >= MAX_ALLERGIES}
        onClick={() => changeRows((current) => [...current, newAllergyRow()])}
      >
        Add allergy
      </Button>
    </ProfileSectionForm>
  );
}
